/**
 * Calendar dates — a timezone-independent day (no time, no zone).
 *
 * On the wire a calendar date is the compact `YYYYMMDD` string the server
 * produces via `dateToYYYYMMDD` (UTC-based). HTML `<input type="date">` and our
 * internal day ids use the dashed `YYYY-MM-DD` form. Every operation here goes
 * through **UTC**, so a calendar date never shifts with the viewer's timezone —
 * this is the fix for the local-midnight drift the old per-file helpers had
 * (e.g. `new Date(y, m-1, d)` parsing a `YYYY-MM-DD` event against a local start
 * could land an event on the wrong day outside UTC).
 *
 * This module is pure: string/number in, string/number out, no clock, no I/O.
 * The interface is the test surface.
 */

/** A calendar date split into fields. `month` is 1-based (1 = January). */
export interface CalendarParts {
	year: number
	month: number
	day: number
}

/**
 * Display styles, all rendered in `en-US` and fixed to UTC so output never
 * depends on the viewer's timezone:
 * - `medium`   → `Jan 15, 2026`
 * - `long`     → `January 15, 2026`
 * - `weekday`  → `Thu, Jan 15`
 * - `monthDay` → `Jan 15`
 */
export type CalendarStyle = 'medium' | 'long' | 'weekday' | 'monthDay'

const DASHED = /^(\d{4})-(\d{2})-(\d{2})$/
const COMPACT = /^(\d{4})(\d{2})(\d{2})$/
const MS_PER_DAY = 24 * 60 * 60 * 1000

const STYLE_OPTS: Record<CalendarStyle, Intl.DateTimeFormatOptions> = {
	medium: { year: 'numeric', month: 'short', day: 'numeric' },
	long: { year: 'numeric', month: 'long', day: 'numeric' },
	weekday: { weekday: 'short', month: 'short', day: 'numeric' },
	monthDay: { month: 'short', day: 'numeric' },
}

const pad = (n: number): string => String(n).padStart(2, '0')

/**
 * Parse a `YYYYMMDD` or `YYYY-MM-DD` string into its fields, or `null` when the
 * input is missing, malformed, or not a real date. Rejects partial/garbage
 * input (`'2026'`) and impossible dates (`'20260231'`) instead of silently
 * coercing them — the bug the old `substring + Number` parsing had.
 */
export const parseCalendarDate = (value: string | null | undefined): CalendarParts | null => {
	if (!value) return null
	const m = DASHED.exec(value) ?? COMPACT.exec(value)
	if (!m) return null
	const year = Number(m[1])
	const month = Number(m[2])
	const day = Number(m[3])
	// Round-trip through UTC so overflow (Feb 31, month 13) is rejected.
	const d = new Date(Date.UTC(year, month - 1, day))
	if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
		return null
	}
	return { year, month, day }
}

/** True when `value` is a valid `YYYYMMDD` or `YYYY-MM-DD` calendar date. */
export const isValidCalendarDate = (value: string | null | undefined): boolean =>
	parseCalendarDate(value) !== null

const toUtcDate = (parts: CalendarParts): Date =>
	new Date(Date.UTC(parts.year, parts.month - 1, parts.day))

const partsFromUtcDate = (date: Date): CalendarParts => ({
	year: date.getUTCFullYear(),
	month: date.getUTCMonth() + 1,
	day: date.getUTCDate(),
})

const dashedOf = (p: CalendarParts): string => `${p.year}-${pad(p.month)}-${pad(p.day)}`
const compactOf = (p: CalendarParts): string => `${p.year}${pad(p.month)}${pad(p.day)}`

/** Normalize any accepted form to compact `YYYYMMDD` (wire format), or `''` if invalid. */
export const toCompact = (value: string | null | undefined): string => {
	const p = parseCalendarDate(value)
	return p ? compactOf(p) : ''
}

/** Normalize any accepted form to dashed `YYYY-MM-DD` (input/internal), or `''` if invalid. */
export const toDashed = (value: string | null | undefined): string => {
	const p = parseCalendarDate(value)
	return p ? dashedOf(p) : ''
}

/** A JS `Date`'s UTC calendar day as compact `YYYYMMDD`. */
export const fromUtcDate = (date: Date): string => compactOf(partsFromUtcDate(date))

/**
 * Format a calendar date for display (UTC, `en-US`), or `''` when invalid so the
 * caller can supply its own fallback (e.g. `formatCalendarDate(d) || 'No dates set'`).
 */
export const formatCalendarDate = (
	value: string | null | undefined,
	style: CalendarStyle = 'medium',
): string => {
	const p = parseCalendarDate(value)
	if (!p) return ''
	return toUtcDate(p).toLocaleDateString('en-US', { ...STYLE_OPTS[style], timeZone: 'UTC' })
}

/** The weekday name for a calendar date (`'long'` → `Monday`, `'short'` → `Mon`), or `''` if invalid. */
export const formatWeekday = (
	value: string | null | undefined,
	width: 'long' | 'short' = 'long',
): string => {
	const p = parseCalendarDate(value)
	if (!p) return ''
	return toUtcDate(p).toLocaleDateString('en-US', { weekday: width, timeZone: 'UTC' })
}

/** `value` shifted by `n` days (may be negative), as dashed `YYYY-MM-DD`, or `''` if invalid. */
export const addDays = (value: string | null | undefined, n: number): string => {
	const p = parseCalendarDate(value)
	if (!p) return ''
	const d = toUtcDate(p)
	d.setUTCDate(d.getUTCDate() + n)
	return dashedOf(partsFromUtcDate(d))
}

/** The calendar date `index` days after `start`, as dashed `YYYY-MM-DD`, or `''` if `start` is invalid. */
export const dateAtDayIndex = (start: string | null | undefined, index: number): string =>
	addDays(start, index)

/**
 * Whole-day offset of `value` from `start` (`value - start`), UTC-stable. Returns
 * `0` when either input is invalid — callers that must distinguish "day 0" from
 * "no dates" should guard with {@link isValidCalendarDate} first.
 */
export const dayIndexOf = (
	value: string | null | undefined,
	start: string | null | undefined,
): number => {
	const v = parseCalendarDate(value)
	const s = parseCalendarDate(start)
	if (!v || !s) return 0
	return Math.round((toUtcDate(v).getTime() - toUtcDate(s).getTime()) / MS_PER_DAY)
}

/** Whole-day difference `end - start` (UTC-stable). Add 1 for an inclusive day count. */
export const daysBetween = (
	start: string | null | undefined,
	end: string | null | undefined,
): number => dayIndexOf(end, start)

/** Inclusive list of every calendar date from `start` to `end` as dashed `YYYY-MM-DD`. Empty if invalid or reversed. */
export const eachDay = (
	start: string | null | undefined,
	end: string | null | undefined,
): string[] => {
	const s = parseCalendarDate(start)
	const e = parseCalendarDate(end)
	if (!s || !e) return []
	const days: string[] = []
	const cur = toUtcDate(s)
	const last = toUtcDate(e)
	while (cur.getTime() <= last.getTime()) {
		days.push(dashedOf(partsFromUtcDate(cur)))
		cur.setUTCDate(cur.getUTCDate() + 1)
	}
	return days
}
